(() => {
  if (customElements.get('dji-credit-estimate')) return;

  const mobileViewport = window.matchMedia('(max-width: 767px)');

  class DjiCreditEstimate extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToCredit) return;
      this.isConnectedToCredit = true;

      this.openButton = this.querySelector('[data-dji-credit-open]');
      this.closeButton = this.querySelector('[data-dji-credit-close]');
      this.dialog = this.querySelector('[data-dji-credit-dialog]');
      this.backdrop = this.querySelector('[data-dji-credit-backdrop]');
      this.amount = this.querySelector('[data-dji-credit-amount]');
      this.productInfo = this.closest('product-info');
      this.percentage = Number.parseFloat(this.dataset.creditPercentage || '0');
      this.closeTimer = null;
      this.previousBodyOverflow = '';
      this.lastFocusedElement = null;

      this.handleOpenClick = this.handleOpenClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleWindowResize = this.handleWindowResize.bind(this);

      this.openButton?.addEventListener('click', this.handleOpenClick);
      this.closeButton?.addEventListener('click', this.handleCloseClick);
      this.backdrop?.addEventListener('click', this.handleCloseClick);
      this.openButton?.addEventListener('mouseenter', this.handlePointerEnter);
      this.openButton?.addEventListener('mouseleave', this.handlePointerLeave);
      this.dialog?.addEventListener('mouseenter', this.handlePointerEnter);
      this.dialog?.addEventListener('mouseleave', this.handlePointerLeave);
      this.openButton?.addEventListener('focus', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.productInfo?.addEventListener('variant:change', this.handleVariantChange, true);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      mobileViewport.addEventListener?.('change', this.handleViewportChange);
      window.addEventListener('resize', this.handleWindowResize);
      this.renderAmount(Number(this.dataset.variantPrice));
    }

    disconnectedCallback() {
      if (!this.isConnectedToCredit) return;

      this.openButton?.removeEventListener('click', this.handleOpenClick);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.backdrop?.removeEventListener('click', this.handleCloseClick);
      this.openButton?.removeEventListener('mouseenter', this.handlePointerEnter);
      this.openButton?.removeEventListener('mouseleave', this.handlePointerLeave);
      this.dialog?.removeEventListener('mouseenter', this.handlePointerEnter);
      this.dialog?.removeEventListener('mouseleave', this.handlePointerLeave);
      this.openButton?.removeEventListener('focus', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.productInfo?.removeEventListener('variant:change', this.handleVariantChange, true);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      mobileViewport.removeEventListener?.('change', this.handleViewportChange);
      window.removeEventListener('resize', this.handleWindowResize);
      window.clearTimeout(this.closeTimer);
      this.unlockBodyScroll();
      this.isConnectedToCredit = false;
    }

    get isOpen() {
      return Boolean(this.dialog && !this.dialog.hidden);
    }

    handleOpenClick() {
      if (mobileViewport.matches && this.isOpen) {
        this.close({ returnFocus: false });
        return;
      }
      this.open({ moveFocus: mobileViewport.matches });
    }

    handleCloseClick() {
      this.close({ returnFocus: true });
    }

    handlePointerEnter() {
      if (mobileViewport.matches) return;
      window.clearTimeout(this.closeTimer);
      this.open({ moveFocus: false });
    }

    handlePointerLeave(event) {
      if (mobileViewport.matches || this.dialog?.contains(event.relatedTarget)) return;
      this.scheduleClose();
    }

    handleFocusIn() {
      if (mobileViewport.matches) return;
      window.clearTimeout(this.closeTimer);
      this.open({ moveFocus: false });
    }

    handleFocusOut(event) {
      if (mobileViewport.matches || this.contains(event.relatedTarget)) return;
      this.scheduleClose();
    }

    handleViewportChange() {
      if (!this.isOpen) return;
      this.close({ returnFocus: false });
    }

    handleWindowResize() {
      if (!this.isOpen) return;
      this.syncDialogArrow();
    }

    handleVariantChange(event) {
      const price = Number(event.detail?.variant?.price);
      if (!Number.isFinite(price)) return;
      this.dataset.variantPrice = String(price);
      this.renderAmount(price);
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      if (event.key !== 'Tab' || !mobileViewport.matches) return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    scheduleClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.close({ returnFocus: false });
      }, 120);
    }

    open({ moveFocus = false } = {}) {
      if (!this.dialog || !this.openButton) return;
      window.clearTimeout(this.closeTimer);
      if (this.isOpen) return;

      this.lastFocusedElement = document.activeElement;
      this.dialog.hidden = false;
      this.openButton.setAttribute('aria-expanded', 'true');
      this.dialog.setAttribute('aria-modal', mobileViewport.matches ? 'true' : 'false');
      this.syncDialogArrow();

      if (mobileViewport.matches) {
        if (this.backdrop) this.backdrop.hidden = false;
        this.lockBodyScroll();
        if (moveFocus) requestAnimationFrame(() => this.closeButton?.focus());
      }
    }

    syncDialogArrow() {
      if (!this.dialog || !this.openButton || window.innerWidth < 1025) return;

      const dialogRect = this.dialog.getBoundingClientRect();
      const buttonRect = this.openButton.getBoundingClientRect();
      const desiredPosition = buttonRect.left + buttonRect.width / 2 - dialogRect.left;
      const arrowPosition = Math.max(14, Math.min(dialogRect.width - 14, desiredPosition));
      this.dialog.style.setProperty('--dji-credit-dialog-arrow-x', `${arrowPosition}px`);
    }

    close({ returnFocus = false } = {}) {
      if (!this.dialog || !this.openButton || !this.isOpen) return;
      window.clearTimeout(this.closeTimer);
      this.dialog.hidden = true;
      if (this.backdrop) this.backdrop.hidden = true;
      this.openButton.setAttribute('aria-expanded', 'false');
      this.dialog.setAttribute('aria-modal', 'false');
      this.unlockBodyScroll();

      if (returnFocus) {
        const focusTarget = this.lastFocusedElement instanceof HTMLElement ? this.lastFocusedElement : this.openButton;
        focusTarget?.focus();
      }
    }

    lockBodyScroll() {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = this.previousBodyOverflow;
      }
    }

    renderAmount(variantPrice) {
      if (!this.amount || !Number.isFinite(this.percentage)) return;
      const creditCents = Math.round(variantPrice * this.percentage / 100);
      const currency = this.dataset.currency || 'MXN';
      const locale = document.documentElement.lang || 'es-MX';
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(creditCents / 100);
      this.amount.textContent = `${formatted} ${currency}`;
    }
  }

  customElements.define('dji-credit-estimate', DjiCreditEstimate);
})();
